
import { Bucket, Idea, UserSettings, User } from '../types';

const API_BASE = '/api';

// Simple check to see if we should use mock data
// We check if the environment is a preview environment or if dev mode is enabled
const isDevMode = () => {
  return typeof window !== 'undefined' && 
    (window.location.hostname === 'localhost' || 
     window.location.hostname.includes('webcontainer') ||
     localStorage.getItem('lifebuckets-dev-mode') === 'true');
};

export const dbService = {
  async login(username: string, password: string): Promise<User | null> {
    if (isDevMode()) {
      console.log('Dev Mode: Mocking login');
      // Always allow 'dev' user or any user in dev mode
      return {
        id: 'dev-user-id',
        username: username || 'dev_explorer',
        email: 'dev@example.com',
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${username}`
      };
    }

    try {
      const response = await fetch(`${API_BASE}/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'login', username, password }),
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (err) {
      console.error('Login error:', err);
      return null;
    }
  },

  async signup(userData: Partial<User & { password?: string }>): Promise<User | null> {
    if (isDevMode()) {
      console.log('Dev Mode: Mocking signup');
      return {
        id: 'dev-user-id',
        username: userData.username || 'dev_explorer',
        email: userData.email || 'dev@example.com',
        avatar: userData.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${userData.username}`
      };
    }

    try {
      const response = await fetch(`${API_BASE}/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'signup', ...userData }),
      });
      if (!response.ok) {
        const msg = await response.text();
        throw new Error(msg);
      }
      return await response.json();
    } catch (err: any) {
      throw err;
    }
  },

  async fetchData(userId: string): Promise<{ buckets?: Bucket[], ideas?: Idea[], settings?: UserSettings } | null> {
    if (isDevMode()) {
      console.log('Dev Mode: Fetching from LocalStorage only');
      return null; // Returning null triggers the seed/local logic in App.tsx
    }

    try {
      const response = await fetch(`${API_BASE}/sync?userId=${userId}`);
      if (!response.ok) throw new Error('Sync failed');
      return await response.json();
    } catch (err) {
      console.warn('D1 Sync unavailable, using local cache:', err);
      return null;
    }
  },

  async pushData(userId: string, data: { buckets?: Bucket[], ideas?: Idea[], settings?: UserSettings }) {
    if (isDevMode()) {
      // In dev mode, data is already saved to local storage by App.tsx, so we just succeed
      return true;
    }

    try {
      const response = await fetch(`${API_BASE}/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, ...data }),
      });
      return response.ok;
    } catch (err) {
      console.error('Failed to push to D1:', err);
      return false;
    }
  }
};
